import { CompactInlineAlertWidget, InlineAlertType, TextAreaWidget, TextFieldWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';

import { IAdminProfileTemplate } from '../models/IAdminProfileTemplate';
import { AdminProfileDialogStyles } from '../styles/AdminProfileDialogStyles';
import { AdminProfileCapabilitiesList } from './AdminProfileCapabilitiesList';

const NAME_TEXT_FIELD_TEST_ID = 'admin-profile-dialog-name';
const ZERO_WIDTH_SPACE = '\u200B';

export type AdminProfileDialogProps = {
  template: IAdminProfileTemplate | null;
  isLoadingTemplate: boolean;
  existingProfileNames: string[];
}

export type AdminProfileDialogState = {
  name: string;
  description: string;
  selectedPermissions: Array<{
    category: string;
    capability: string;
  }>;
  errorMessage?: string;
}

export type AdminProfileActionDialogProps = ReactActionDialogBodyProps<
  AdminProfileDialogProps,
  AdminProfileDialogState
>;

export function AdminProfileDialogBody(props: AdminProfileActionDialogProps): JSX.Element {
  const {
    template,
    isLoadingTemplate,
    existingProfileNames,
  } = props;
  const {
    name,
    description,
    selectedPermissions,
    errorMessage,
  } = props.dialogState;

  const isDuplicateName = name.trim().length > 0 && existingProfileNames.some(
    existing => existing.toLowerCase() === name.trim().toLowerCase()
  );
  const nameErrorMessage = isDuplicateName ? Messages.createAdminProfileNameDuplicate() : undefined;

  const togglePermission = (category: string, capability: string, checked: boolean) => {
    const newPermissions = selectedPermissions.slice();
    if (checked) {
      newPermissions.push({ category, capability });
    } else {
      const idx = newPermissions.findIndex(
        p => p.category === category && p.capability === capability
      );
      if (idx >= 0) {
        newPermissions.splice(idx, 1);
      }
    }
    props.updateDialogState({ selectedPermissions: newPermissions });
  };

  const toggleSelectAll = (category: string, capabilities: string[]) => {
    const selectedInCategory = selectedPermissions.filter(p => p.category === category);
    const allSelected = selectedInCategory.length === capabilities.length;

    let newPermissions = selectedPermissions.filter(p => p.category !== category);
    if (!allSelected) {
      const allForCategory = capabilities.map(cap => ({ category, capability: cap }));
      newPermissions = newPermissions.concat(allForCategory);
    }
    props.updateDialogState({ selectedPermissions: newPermissions });
  };

  const isPermissionSelected = React.useCallback(
    (category: string, capability: string): boolean => {
      return selectedPermissions.some(
        p => p.category === category && p.capability === capability
      );
    },
    [selectedPermissions]
  );

  const onNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      props.doAction();
    }
  };

  if (isLoadingTemplate) {
    return <div className={AdminProfileDialogStyles.CenterMessage}>{Messages.createAdminProfileLoadingTemplate()}</div>;
  }

  if (!template) {
    return <div className={AdminProfileDialogStyles.CenterMessage}>{Messages.createAdminProfileLoadTemplateFailed()}</div>;
  }

  return (
    <div className={AdminProfileDialogStyles.DialogBody}>
      {/* Admin Profile Name */}
      <div className={AdminProfileDialogStyles.Section}>
        <div className={AdminProfileDialogStyles.SectionLabel}>{Messages.createAdminProfileNameLabel()}</div>
        <TextFieldWidget
          className={AdminProfileDialogStyles.NameField}
          kind='outline'
          label={''}
          aria-label={Messages.createAdminProfileNameLabel()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.updateDialogState({ name: e.target.value })}
          onKeyDown={onNameKeyDown}
          spellCheck={false}
          testId={NAME_TEXT_FIELD_TEST_ID}
          value={name}
          placeholder={Messages.createAdminProfileNamePlaceholder()}
          message={nameErrorMessage || ZERO_WIDTH_SPACE}
          valid={nameErrorMessage ? false : undefined}
          disabled={props.actionBusy}
        />
      </div>

      {/* Permissions */}
      <div className={AdminProfileDialogStyles.Section}>
        <div className={AdminProfileDialogStyles.SectionLabel}>{Messages.createAdminProfilePermissionsHeader()}</div>
        <AdminProfileCapabilitiesList
          permissions={template.permissions}
          isCapabilityEnabled={isPermissionSelected}
          onToggleCapability={togglePermission}
          onSelectAll={toggleSelectAll}
          disabled={props.actionBusy}
          testIdPrefix="capability-hint"
        />
      </div>

      {/* Description (optional) */}
      <div className={AdminProfileDialogStyles.Section}>
        <div className={AdminProfileDialogStyles.SectionLabel}>{Messages.createAdminProfileDescriptionOptional()}</div>
        <TextAreaWidget
          className={AdminProfileDialogStyles.DescriptionTextarea}
          value={description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ description: e.target.value })}
          label={''}
          aria-label={Messages.createAdminProfileDescriptionOptional()}
          disabled={props.actionBusy}
          testId="admin-profile-dialog-description"
        />
      </div>

      {/* Total permissions selected */}
      <div className={AdminProfileDialogStyles.TotalSelected}>
        {Messages.createAdminProfileTotalSelected({ COUNT: String(selectedPermissions.length) })}
      </div>

      {/* Error message */}
      {errorMessage && (
        <CompactInlineAlertWidget
          type={InlineAlertType.Error}
          testId="admin-profile-dialog-error"
        >
          {errorMessage}
        </CompactInlineAlertWidget>
      )}
    </div>
  );
}
